import { useDispatch, useSelector } from "react-redux";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Select from "react-select";
import DatePicker from "react-date-picker";
import moment from "moment";
import { ConvertErrorArrayToString } from "../../Services/Common";
import { setToastData } from "../../Store/reducers/Toast";

const AddCollection = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accessToken, userData } = useSelector((state) => state.user);
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);
    const [filter, setFilter] = useState({
        org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
    });

    useEffect(() => {
        dispatch(setPageTitle("Add Collection"));
        const promiseArray = [];
        if (userData.role_slug === "super_admin") {
            promiseArray.push(PostAxios(`masters/organization`, { page: 1, perPage: 1000 }, accessToken));
        }
        if (userData.role_slug === "super_admin" || userData.role_slug === "org_admin") {
            promiseArray.push(PostAxios(`masters/division-list`, {
                org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
                page: 1,
                perPage: 1000,
            }, accessToken));
        }
        if (promiseArray.length > 0) {
            dispatch(setMainLoading(true));
            Promise.all(promiseArray)
                .then((response) => {
                    if (response[0]?.config?.url === "masters/organization" && response[0].data.status === "1") {
                        setOrgList(response[0].data.data);
                    } else if (response[0]?.data?.status === "1") {
                        setDivList(response[0].data.data);
                    }
                    if (response[1]?.config?.url === "masters/division-list" && response[1].data.status === "1") {
                        setDivList(response[1].data.data);
                    }
                })
                .catch((err) => console.log(err))
                .finally(() => dispatch(setMainLoading(false)));
        }
    }, [accessToken, dispatch, userData]);

    const divListForSelect2 = useMemo(() => {
        if (parseInt(filter.org_id, 10) > 0) {
            return divList
                .filter((ml) => parseInt(ml.org_id, 10) === parseInt(filter.org_id, 10))
                .map((ml) => ({ value: ml.ID, label: `${ml.division_name} (${ml.division_number})` }));
        }
        return [];
    }, [filter.org_id, divList]);

    return (
        <div className="main-form-sec">
            <Formik
                initialValues={{
                    org_id: parseInt(userData.org_id, 10) > 0 ? parseInt(userData.org_id, 10) : 0,
                    div_id: [],
                    collection_name: "",
                    collection_description: "",
                    target_amount: "",
                    end_date: "",
                    collection_type: "monthly",
                    collection_frequency: "monthly",
                    status: 1,
                }}
                validate={(values) => {
                    const errors = {};
                    if (parseInt(values.org_id, 10) < 1) errors.org_id = "Organization is required";
                    if (!values.div_id.length) errors.div_id = "Please select at least one division";
                    if (!values.collection_name.trim()) errors.collection_name = "Collection name is required";
                    if (!values.target_amount || parseFloat(values.target_amount) < 0) errors.target_amount = "Target amount is required";
                    if (!values.end_date) errors.end_date = "End date is required";
                    return errors;
                }}
                onSubmit={(values, { setSubmitting }) => {
                    dispatch(setMainLoading(true));
                    setSubmitting(true);
                    const divIds = values.div_id.map((d) => d.value).join(",");
                    PostAxios(`collection/add-collection`, {
                        org_id: values.org_id,
                        div_id: divIds,
                        collection_name: values.collection_name,
                        collection_description: values.collection_description,
                        target_amount: values.target_amount,
                        end_date: moment(values.end_date).format("YYYY-MM-DD"),
                        collection_type: values.collection_type,
                        collection_frequency: values.collection_frequency,
                        status: values.status,
                    }, accessToken)
                        .then(({ data }) => {
                            if (data.status === "1") {
                                dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                                navigate("/collections");
                            } else {
                                dispatch(setToastData({
                                    message: data.errors ? ConvertErrorArrayToString(data.errors) : data.message,
                                    type: "error",
                                    showCloseBtn: true,
                                }));
                            }
                        })
                        .catch((err) => responseCatchFunc(dispatch, err))
                        .finally(() => {
                            setSubmitting(false);
                            dispatch(setMainLoading(false));
                        });
                }}
            >
                {({ errors, touched, isSubmitting, handleChange, setFieldValue, values }) => (
                    <Form>
                        <div className="form-sec">
                            {userData.role_slug === "super_admin" && (
                                <div className={`form-field-group w-50 ${errors.org_id && touched.org_id ? "error-input" : ""}`}>
                                    <div className="w-50"><label htmlFor="org_id">Organization*</label></div>
                                    <div className="w-50">
                                        <Field
                                            name="org_id"
                                            as="select"
                                            className="select-input"
                                            onChange={(e) => {
                                                setFilter({ org_id: e.target.value });
                                                setFieldValue("div_id", []);
                                                handleChange(e);
                                            }}
                                        >
                                            <option value="0">Choose Organization</option>
                                            {orgList.map((item) => (
                                                <option key={`org-${item.ID}`} value={item.ID}>{item.short_name}</option>
                                            ))}
                                        </Field>
                                        <ErrorMessage component="span" name="org_id" className="error-label" />
                                    </div>
                                </div>
                            )}
                            {(userData.role_slug === "super_admin" || userData.role_slug === "org_admin") && (
                                <div className={`form-field-group w-50 ${errors.div_id && touched.div_id ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Division*</label></div>
                                    <div className="w-50 input-area text-left">
                                        <Select
                                            options={[{ label: "Select All", value: "select_all" }, ...divListForSelect2]}
                                            placeholder="Choose Division"
                                            value={values.div_id}
                                            onChange={(selectedValues) => {
                                                if (selectedValues.some((option) => option.value === "select_all")) {
                                                    setFieldValue("div_id", divListForSelect2);
                                                } else {
                                                    setFieldValue("div_id", selectedValues);
                                                }
                                            }}
                                            isSearchable
                                            isMulti
                                        />
                                        <ErrorMessage component="span" name="div_id" className="error-label" />
                                    </div>
                                </div>
                            )}
                            <div className={`form-field-group w-50 ${errors.collection_name && touched.collection_name ? "error-input" : ""}`}>
                                <div className="w-50"><label>Collection Name*</label></div>
                                <div className="w-50 input-area">
                                    <Field name="collection_name" className="form-input" placeholder="Collection Name" />
                                    <ErrorMessage component="span" name="collection_name" className="error-label" />
                                </div>
                            </div>
                            <div className="form-field-group w-50">
                                <div className="w-50"><label>Description</label></div>
                                <div className="w-50 input-area">
                                    <Field as="textarea" name="collection_description" className="form-input" rows="3" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.target_amount && touched.target_amount ? "error-input" : ""}`}>
                                <div className="w-50"><label>Target Amount*</label></div>
                                <div className="w-50 input-area">
                                    <Field name="target_amount" type="number" min="0" step="0.01" className="form-input" />
                                    <ErrorMessage component="span" name="target_amount" className="error-label" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.end_date && touched.end_date ? "error-input" : ""}`}>
                                <div className="w-50"><label>Collection End Date*</label></div>
                                <div className="w-50">
                                    <div className="date-picker-sec">
                                        <DatePicker
                                            value={values.end_date}
                                            onChange={(date) => setFieldValue("end_date", date)}
                                            format="dd-MM-y"
                                            minDate={new Date()}
                                            dayPlaceholder="DD"
                                            monthPlaceholder="MM"
                                            yearPlaceholder="YYYY"
                                            className="form-input"
                                        />
                                    </div>
                                    <ErrorMessage component="span" name="end_date" className="error-label" />
                                </div>
                            </div>
                            <div className="form-field-group w-50">
                                <div className="w-50"><label>Collection Type*</label></div>
                                <div className="w-50">
                                    <Field name="collection_type" as="select" className="select-input">
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </Field>
                                </div>
                            </div>
                            <div className="form-field-group w-50">
                                <div className="w-50"><label>Collection Frequency*</label></div>
                                <div className="w-50">
                                    <Field name="collection_frequency" as="select" className="select-input">
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </Field>
                                </div>
                            </div>
                            <div className="form-field-group w-50">
                                <div className="w-50"><label>Status*</label></div>
                                <div className="w-50">
                                    <Field name="status" as="select" className="select-input">
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </Field>
                                </div>
                            </div>
                        </div>
                        <div className="form-sec">
                            <button type="submit" className="btn btn-blue" disabled={isSubmitting}>Save Collection</button>
                            <button type="button" className="btn btn-blue ms-2" onClick={() => navigate("/collections")}>Cancel</button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddCollection;
